import { lazy, Suspense } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';

import PointsLayout from 'src/layouts/points/points-layout';
import NotificationHeaderLayout from 'src/layouts/notification/notification-header-layout';

import { SplashScreen } from 'src/components/loading-screen';

import { paths } from '../paths';
import { authRoutes } from './auth';
import { errorRoutes } from './error';
import { AuthGuard } from '../../auth/guard';
import { DefaultLayout } from '../../layouts/index';

// PAGE_IMPORTS
const Benefits = lazy(() => import('../../modules/benefits/benefits/benefits'));
const NavigationMenu = lazy(
  () => import('../../modules/navigation-menu/navigation-menu/navigation-menu')
);
const CollectibleRescue = lazy(
  () => import('../../modules/benefits/collectible-rescue/collectible-rescue')
);
const BenefitsList = lazy(() => import('../../modules/benefits/benefits-list/benefits-list'));
const MissionInstant = lazy(() => import('../../modules/missions/mission-instant/mission-instant'));
const CarPlateResult = lazy(
  () => import('../../modules/missions/car-plate-result/car-plate-result')
);
const MissionCarPlate = lazy(
  () => import('../../modules/missions/mission-car-plate/mission-car-plate')
);
const OfferDetails = lazy(() => import('../../modules/benefits/offer-details/offer-details'));
const SimplifiedLp = lazy(() => import('../../modules/help/simplified-lp/simplified-lp'));
const MyInterestsResult = lazy(
  () => import('../../modules/my-interests/my-interests-result/my-interests-result')
);
const MyInterestsQuiz = lazy(
  () => import('../../modules/my-interests/my-interests-quiz/my-interests-quiz')
);
const MyInterestsInvite = lazy(
  () => import('../../modules/my-interests/my-interests-invite/my-interests-invite')
);
const PromotionalActionDetails = lazy(
  () => import('../../modules/help/promotional-action-details/promotional-action-details')
);
const RegulationDetails = lazy(
  () => import('../../modules/help/regulation-details/regulation-details')
);
const Regulation = lazy(() => import('../../modules/help/regulation/regulation'));
const MemoryGameResult = lazy(
  () => import('../../modules/games/memory-game-result/memory-game-result')
);
const MemoryGameDetails = lazy(
  () => import('../../modules/games/memory-game-details/memory-game-details')
);
const MemoryGame = lazy(() => import('../../modules/games/memory-game/memory-game'));
const OnboardingEnding = lazy(
  () => import('../../modules/onboarding/onboarding-ending/onboarding-ending')
);
const OnboardingSteps = lazy(
  () => import('../../modules/onboarding/onboarding-steps/onboarding-steps')
);
const Onboarding = lazy(() => import('../../modules/onboarding/onboarding/onboarding'));
const QuizResult = lazy(() => import('../../modules/missions/quiz-result/quiz-result'));
const MissionQuizQuestions = lazy(
  () => import('../../modules/missions/mission-quiz-questions/mission-quiz-questions')
);
const MissionQuiz = lazy(() => import('../../modules/missions/mission-quiz/mission-quiz'));
const MissionCheckIn = lazy(
  () => import('../../modules/missions/mission-check-in/mission-check-in')
);
const MissionCustomScan = lazy(
  () => import('../../modules/missions/mission-custom-scan/mission-custom-scan')
);
const MissionCustomScanProgress = lazy(
  () => import('../../modules/missions/mission-custom-scan-progress/mission-custom-scan-progress')
);
const MissionCustomScanResult = lazy(
  () => import('../../modules/missions/mission-custom-scan-result/mission-custom-scan-result')
);
const MissionCustomScanV2 = lazy(
  () => import('../../modules/missions/mission-custom-scan-v2/mission-custom-scan-v2')
);
const MissionCustomScanProgressV2 = lazy(
  () =>
    import('../../modules/missions/mission-custom-scan-progress-v2/mission-custom-scan-progress-v2')
);
const MissionCustomScanResultV2 = lazy(
  () => import('../../modules/missions/mission-custom-scan-result-v2/mission-custom-scan-result-v2')
);
const MissionCustomScanResultV3 = lazy(
  () => import('../../modules/missions/mission-custom-scan-result-v3/mission-custom-scan-result-v3')
);
const MissionCustomScanV3 = lazy(
  () => import('../../modules/missions/mission-custom-scan-v3/mission-custom-scan-v3')
);
const MissionCustomScanProgressV3 = lazy(
  () =>
    import('../../modules/missions/mission-custom-scan-progress-v3/mission-custom-scan-progress-v3')
);
const MissionInvoice = lazy(() => import('../../modules/missions/mission-invoice/mission-invoice'));
const MissionsList = lazy(() => import('../../modules/missions/missions-list/missions-list'));
const Search = lazy(() => import('../../modules/search/search/search'));
const SearchResults = lazy(() => import('../../modules/search/search-results/search-results'));
const MyPromotions = lazy(() => import('../../modules/benefits/my-promotions/my-promotions'));
const PromotionDetails = lazy(
  () => import('../../modules/benefits/promotion-details/promotion-details')
);
const Promotions = lazy(() => import('../../modules/benefits/promotions/promotions'));
const AboutNotes = lazy(() => import('../../modules/help/about-notes/about-notes'));
const AboutPoints = lazy(() => import('../../modules/help/about-points/about-points'));
const AboutLevels = lazy(() => import('../../modules/help/about-levels/about-levels'));
const Faq = lazy(() => import('../../modules/help/faq/faq'));
const CollectibleDetails = lazy(
  () => import('../../modules/benefits/collectible-details/collectible-details')
);
const Collectibles = lazy(() => import('../../modules/benefits/collectibles/collectibles'));
const MyLevel = lazy(() => import('../../modules/user/my-level/my-level'));
const MyLevelBenefits = lazy(
  () => import('../../modules/user/my-level-benefits/my-level-benefits')
);
const MyNotesInformation = lazy(
  () => import('../../modules/user/my-notes-information/my-notes-information')
);
const PointsHowToEarn = lazy(
  () => import('../../modules/user/points-how-to-earn/points-how-to-earn')
);
const PointsHowToUse = lazy(() => import('../../modules/user/points-how-to-use/points-how-to-use'));
const MyNotes = lazy(() => import('../../modules/user/my-notes/my-notes'));
const PointDetails = lazy(() => import('../../modules/user/point-details/point-details'));
const PointsHistory = lazy(() => import('../../modules/user/points-history/points-history'));
const Points = lazy(() => import('../../modules/user/points/points'));
const EventSchedule = lazy(() => import('../../modules/benefits/event-schedule/event-schedule'));
const EventScheduled = lazy(() => import('../../modules/benefits/event-scheduled/event-scheduled'));
const AboutEvent = lazy(() => import('../../modules/benefits/about-event/about-event'));
const MyEvents = lazy(() => import('../../modules/benefits/my-events/my-events'));
const Events = lazy(() => import('../../modules/benefits/events/events'));
const MyProfile = lazy(() => import('../../modules/user/my-profile/my-profile'));
const DeleteAccount = lazy(() => import('../../modules/user/delete-account/delete-account'));
const RegistrationData = lazy(
  () => import('../../modules/user/registration-data/registration-data')
);
const HomolMotor = lazy(() => import('../../modules/test/homol-motor/homol-motor'));
const MyExchanges = lazy(() => import('../../modules/user/my-exchanges/my-exchanges'));
const AllProducts = lazy(() => import('../../modules/benefits/all-products/all-products'));
const ProductRescue = lazy(() => import('../../modules/benefits/product-rescue/product-rescue'));
const ProductDetails = lazy(() => import('../../modules/benefits/product-details/product-details'));
const BenefitsRescue = lazy(() => import('../../modules/benefits/discount-rescue/discount-rescue'));
const BenefitsDetails = lazy(
  () => import('../../modules/benefits/discount-details/discount-details')
);
const CouponRescue = lazy(() => import('../../modules/benefits/coupon-rescue/coupon-rescue'));
const CouponDetails = lazy(() => import('../../modules/benefits/coupon-details/coupon-details'));
const AllDiscountsCoupons = lazy(
  () => import('../../modules/benefits/all-discounts-coupons/all-discounts-coupons')
);
const AllBenefitsCoupons = lazy(
  () => import('../../modules/benefits/all-benefits-coupons/all-benefits-coupons')
);
const Coupons = lazy(() => import('../../modules/benefits/coupons/coupons'));
const ExperienceRescue = lazy(
  () => import('../../modules/benefits/experience-rescue/experience-rescue')
);
const ExperienceDetails = lazy(
  () => import('../../modules/benefits/experience-details/experience-details')
);
const Experiences = lazy(() => import('../../modules/benefits/experiences/experiences'));
const Home = lazy(() => import('../../modules/home/home/home'));
const Test = lazy(() => import('../../modules/test/test/test'));
const AuthError = lazy(() => import('../../modules/auth/auth-error/auth-error'));
const Register = lazy(() => import('../../modules/auth/register/register'));

export default function Router() {
  const location = useLocation();
  return useRoutes([
    {
      path: '/',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <Navigate to={`${paths.home.root}${location.search}`} replace />
          </DefaultLayout>
        </Suspense>
      ),
    },
    ...authRoutes,

    // RELATION_PATH_PAGES
    {
      path: paths.benefits.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Benefits />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.navigationMenu.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <NavigationMenu />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.collectibles.rescue(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <CollectibleRescue />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.benefitsList.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <BenefitsList />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionInstant.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionInstant />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.carPlateResult.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <CarPlateResult />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCarPlate.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCarPlate />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.offerDetails.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <OfferDetails />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.simplifiedLp.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <SimplifiedLp />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myInterestsResult.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyInterestsResult />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myInterestsQuiz.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyInterestsQuiz />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myInterestsInvite.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyInterestsInvite />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.regulation.promotionalAction(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <PromotionalActionDetails />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.regulation.details(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <RegulationDetails />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.regulation.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Regulation />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.games.memoryGameResult(':id'),
      element: (
        <AuthGuard>
          <DefaultLayout>
            <MemoryGameResult />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.games.memoryGameDetails(':id'),
      element: (
        <AuthGuard>
          <DefaultLayout>
            <MemoryGameDetails />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.games.memoryGame(':id'),
      element: (
        <AuthGuard>
          <DefaultLayout>
            <MemoryGame />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.aboutNotes.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <AboutNotes />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.aboutPoints.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <AboutPoints />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.aboutLevels.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <AboutLevels />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.faq.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Faq />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.onboardingEnding.root,
      element: (
        <AuthGuard>
          <DefaultLayout>
            <OnboardingEnding />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.onboardingSteps.root,
      element: (
        <AuthGuard>
          <DefaultLayout>
            <OnboardingSteps />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.onboarding.root,
      element: (
        <AuthGuard>
          <DefaultLayout>
            <Onboarding />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.quizResult.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <QuizResult />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionQuizQuestions.root(':id'),
      element: (
        <AuthGuard>
          <DefaultLayout>
            <MissionQuizQuestions />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionQuiz.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionQuiz />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCheckIn.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCheckIn />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionInvoice.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionInvoice />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCustomScan.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCustomScan />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCustomScanProgress.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCustomScanProgress />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCustomScanResult.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCustomScanResult />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCustomScanV2.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCustomScanV2 />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCustomScanV2Progress.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCustomScanProgressV2 />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCustomScanV3Progress.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCustomScanProgressV3 />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCustomScanResultV2.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCustomScanResultV2 />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCustomScanResultV3.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCustomScanResultV3 />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionCustomScanV3.root(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionCustomScanV3 />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.missionsList.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MissionsList />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.search.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Search />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.search.results,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <SearchResults />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.promotions.my,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyPromotions />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.promotions.details(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <PromotionDetails />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.promotions.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Promotions />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myLevel.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyLevel />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.collectibles.collectibleDetails(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <CollectibleDetails />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.collectibles.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Collectibles />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myLevel.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyLevel />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myLevelBenefits.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyLevelBenefits />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myNotesInformation.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyNotesInformation />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.pointsHowToEarn.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <PointsHowToEarn />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.pointsHowToUse.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <PointsHowToUse />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myNotes.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyNotes />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.pointsHistory.pointsDetails(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <PointDetails />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.pointsHistory.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <PointsHistory />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.points.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Points />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.events.schedule(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <EventSchedule />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.events.scheduled(':id', ':scheduleId'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <EventScheduled />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.events.about(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <AboutEvent />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.events.my,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyEvents />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.events.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Events />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myProfile.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyProfile />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.myLevelBenefits.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyLevelBenefits />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.deleteAccount.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <DeleteAccount />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.registrationData.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <RegistrationData />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.homolMotor.root,
      element: (
        <DefaultLayout>
          <HomolMotor />
        </DefaultLayout>
      ),
    },
    {
      path: paths.myExchanges.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <MyExchanges />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.products.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <AllProducts />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.products.rescue(':id', ':redemptionId'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <ProductRescue />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.products.details(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <ProductDetails />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.experiences.rescue(':id'),
      element: (
        <AuthGuard>
          <PointsLayout>
            <DefaultLayout>
              <ExperienceRescue />
            </DefaultLayout>
          </PointsLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.coupons.benefitsRescue(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <BenefitsRescue />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.coupons.benefitsDetails(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <BenefitsDetails />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.coupons.couponRescue(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <CouponRescue />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.coupons.couponDetails(':id'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <CouponDetails />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.coupons.discounts,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <AllDiscountsCoupons />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.coupons.benefits(':type'),
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <AllBenefitsCoupons />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.coupons.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Coupons />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.experiences.rescue(':id'),
      element: (
        <AuthGuard>
          <PointsLayout>
            <DefaultLayout>
              <ExperienceRescue />
            </DefaultLayout>
          </PointsLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.experiences.details(':id'),
      element: (
        <AuthGuard>
          <PointsLayout>
            <DefaultLayout>
              <ExperienceDetails />
            </DefaultLayout>
          </PointsLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.experiences.root,
      element: (
        <AuthGuard>
          <PointsLayout>
            <DefaultLayout>
              <Experiences />
            </DefaultLayout>
          </PointsLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.home.root,
      element: (
        <AuthGuard>
          <NotificationHeaderLayout>
            <DefaultLayout>
              <Home />
            </DefaultLayout>
          </NotificationHeaderLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.test.root,
      element: (
        <DefaultLayout>
          <Test />
        </DefaultLayout>
      ),
    },
    {
      path: paths.authError.root,
      element: (
        <DefaultLayout>
          <AuthError />
        </DefaultLayout>
      ),
    },
    {
      path: paths.register.root,
      element: (
        <DefaultLayout>
          <Register />
        </DefaultLayout>
      ),
    },

    { path: '*', element: <Navigate to="/404" replace /> },

    ...errorRoutes,
  ]);
}
